/**
 * useNotificationVisibility — canonical primitive for emitting
 * `notification_shown` AppInsights customEvents when a notification,
 * alert, banner, or status nudge becomes visible to the user.
 *
 * Lives in its own module (separate from lib/telemetry.ts) so that:
 *   1. Test code can `vi.mock('@/lib/telemetry')` to intercept the
 *      trackNotification call without losing the hook itself.
 *   2. The hook can be imported without pulling in the heavy
 *      ApplicationInsights SDK at module-init time when only the
 *      type is needed.
 *
 * AB#19124 slice 4. See lib/telemetry.ts for design rationale.
 */

import { useEffect, useRef } from 'react';
import { trackNotification, type NotificationKind } from './telemetry';

/**
 * Fire `notification_shown` exactly once when `visible` toggles to true.
 *
 *   - Re-renders with the same visible=true value do NOT re-emit.
 *   - A toggle to false and back to true does re-emit (it's a new
 *     appearance).
 *   - Prop changes while staying visible do NOT re-emit. Conservative
 *     semantic — surfaces that mutate frequently (countdowns, status
 *     polling) shouldn't flood customEvents. If a real new notification
 *     needs to fire, the parent should toggle `visible` false→true.
 *
 * Fires AT state-change time (before any enter animation), so a
 * fast-dismissed notification still counts and the telemetry timing is
 * decoupled from motion implementation. Honors `prefers-reduced-motion`
 * implicitly — no transition required for the event to fire.
 */
export function useNotificationVisibility(surface: string, kind: NotificationKind, visible: boolean, props?: Record<string, string>) {
  const firedRef = useRef(false);
  // Stringify props so the effect only re-runs when their values change,
  // not when the caller passes a fresh object literal each render.
  const propsKey = props ? JSON.stringify(props) : '';
  useEffect(() => {
    if (!visible) {
      firedRef.current = false;
      return;
    }
    if (firedRef.current) return;
    firedRef.current = true;
    trackNotification(surface, kind, props);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, surface, kind, propsKey]);
}